// Phase 5.7 · 소셜 채널 연결 상태 카드 · 판매자 대시보드/라이브 콘솔에서 사용
// - 기존 endpoint 재사용: GET /seller/live/channels
// - 판매자에게 개발자 정보 노출 최소화, 간단한 상태만 표시
import React, { useEffect, useState, useCallback } from 'react';
import { Radio, CheckCircle2, Circle, ExternalLink, RefreshCw, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function LiveChannelsCard({ compact = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ttInput, setTtInput] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get('/seller/live/channels');
      setData(r.data);
    } catch { /* silent */ }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const registerTikTok = async () => {
    const v = (ttInput || '').trim().replace(/^@/, '');
    if (!v) { toast.error('TikTok unique_id 를 입력해주세요.'); return; }
    setBusy(true);
    try {
      await api.post('/seller/live/tiktok/register', { unique_id: v });
      toast.success(`TikTok @${v} 연결됨`);
      setTtInput('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '연결 실패'); }
    finally { setBusy(false); }
  };

  const detectYouTube = async () => {
    setBusy(true);
    try {
      const r = await api.post('/seller/live/youtube/autodetect');
      if (r.data?.connected) {
        toast.success(r.data.current_live_id ? '새 LIVE 방송을 감지했습니다.' : 'YouTube 연결 확인 · 현재 LIVE 없음');
      } else { toast.error(r.data?.detail || 'YouTube 미연결'); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '감지 실패'); }
    finally { setBusy(false); }
  };

  const detectTikTok = async () => {
    setBusy(true);
    try {
      const r = await api.post('/seller/live/tiktok/autodetect');
      if (!r.data?.connected) {
        toast.error(r.data?.detail || 'TikTok 미연결');
      } else if (r.data.action === 'already_live') {
        toast.info('이미 감지된 LIVE 세션이 있습니다.');
      } else if (r.data.action === 'started') {
        toast.success('TikTok LIVE 감지 · 세션 시작');
      } else if (r.data.action === 'production_pending') {
        toast.warning('Production API 연결 대기');
      } else {
        toast.success('TikTok 감지 완료');
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '감지 실패'); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const yt = data.youtube || {};
  const tt = data.tiktok || {};

  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-3 md:p-4" data-testid="live-channels-card">
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-sm font-black tracking-widest uppercase inline-flex items-center gap-1.5">
          <Radio size={14} /> 소셜 채널 연결
        </p>
        <button onClick={load} disabled={busy}
          className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 hover:text-[#FF3B30]" data-testid="live-channels-refresh">
          <RefreshCw size={10} className={busy ? 'animate-spin' : ''} /> 새로고침
        </button>
      </div>

      <div className="space-y-3">
        {/* YouTube */}
        <div className="rounded-lg border border-black/5 dark:border-white/5 p-3" data-testid="live-channel-youtube">
          <div className="flex items-center gap-2 flex-wrap">
            {yt.connected
              ? <CheckCircle2 size={14} className="text-emerald-600" />
              : <Circle size={14} className="text-gray-300" />}
            <span className="text-sm font-black">YouTube</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${yt.connected ? 'bg-emerald-500/10 text-emerald-700' : 'bg-gray-500/10 text-gray-500'}`}>
              {yt.connected ? '● 연결됨' : '○ 미연결'}
            </span>
            {yt.connected && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${yt.current_live_id ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-black/5 dark:bg-white/10 text-gray-500'}`}>
                {yt.current_live_id ? '● 현재 LIVE 감지됨' : '자동 감지 대기중'}
              </span>
            )}
            {yt.connected && (
              <button onClick={detectYouTube} disabled={busy} className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-[#FF3B30] hover:underline" data-testid="live-yt-detect-btn">
                <Zap size={10} /> 지금 감지
              </button>
            )}
          </div>
          {!compact && yt.channel_id && (
            <p className="mt-1 text-[10px] text-gray-500">채널 {yt.channel_id.slice(0, 20)}…</p>
          )}
        </div>

        {/* TikTok */}
        <div className="rounded-lg border border-black/5 dark:border-white/5 p-3" data-testid="live-channel-tiktok">
          <div className="flex items-center gap-2 flex-wrap">
            {tt.connected
              ? <CheckCircle2 size={14} className="text-emerald-600" />
              : <Circle size={14} className="text-gray-300" />}
            <span className="text-sm font-black">TikTok</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${tt.connected ? 'bg-emerald-500/10 text-emerald-700' : 'bg-gray-500/10 text-gray-500'}`}>
              {tt.connected ? `● @${tt.unique_id}` : '○ 미연결'}
            </span>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700" data-testid="live-tt-mode">
              {tt.production_ready ? 'PRODUCTION' : `${(tt.mode || 'mock').toUpperCase()} 모드 · API 실연동 전`}
            </span>
            {tt.current_live && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FF3B30]/10 text-[#FF3B30]">● 현재 LIVE</span>
            )}
            {tt.connected && (
              <button onClick={detectTikTok} disabled={busy} className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-[#FF3B30] hover:underline" data-testid="live-tt-detect-btn">
                <Zap size={10} /> 지금 감지
              </button>
            )}
          </div>
          {!tt.connected && (
            <div className="mt-2 flex gap-1.5">
              <input value={ttInput} onChange={(e) => setTtInput(e.target.value)} placeholder="@yourhandle"
                className="flex-1 px-2 py-1 text-xs rounded border border-black/10 dark:border-white/10 bg-white dark:bg-black" data-testid="live-tt-input" />
              <button onClick={registerTikTok} disabled={busy || !ttInput.trim()}
                className="px-3 py-1 text-xs font-bold rounded bg-[#FF3B30] text-white disabled:opacity-50" data-testid="live-tt-register">
                연결
              </button>
            </div>
          )}
        </div>
      </div>

      <p className="mt-2 text-[10px] text-gray-400">※ TikTok 유료 실연동은 개발 막바지 별도 테스트. 현재는 Mock/TEST 모드로 이벤트 파이프라인 준비 완료.</p>
    </div>
  );
}
